import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { QuickLogSheet } from "@/components/QuickLogSheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { Plus, LayoutDashboard, Bug, Truck, Package, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
type AppLayoutProps = {
  children: React.ReactNode;
};
const mobileNavItems = [
  { label: "Dashboard", href: "/", icon: LayoutDashboard },
  { label: "Animals", href: "/animals", icon: Bug },
  { label: "Vehicles", href: "/vehicles", icon: Truck },
  { label: "Inventory", href: "/inventory", icon: Package },
  { label: "Docs", href: "/documents", icon: FileText },
];
export function AppLayout({ children }: AppLayoutProps): JSX.Element {
  const [quickLogOpen, setQuickLogOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  return (
    <SidebarProvider defaultOpen={!isMobile}>
      <AppSidebar />
      <SidebarInset className="flex flex-col min-h-screen">
        <header className="sticky top-0 z-10 flex h-12 items-center gap-2 border-b bg-background/95 backdrop-blur px-4">
          <SidebarTrigger />
          <div className="flex-1" />
        </header>
        <main className="flex-1 pb-20 md:pb-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-6 md:py-8">
              {children}
            </div>
          </div>
        </main>
        {/* Quick Log FAB */}
        <button
          onClick={() => setQuickLogOpen(true)}
          className={cn(
            "fixed z-30 flex h-14 w-14 items-center justify-center rounded-full bg-burnt-orange dark:bg-burnt-orange-dark text-white shadow-lg",
            "hover:bg-burnt-orange/90 dark:hover:bg-burnt-orange-dark/90 hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200",
            "focus:outline-none focus:ring-2 focus:ring-ranch-green focus:ring-offset-2",
            isMobile ? "bottom-20 right-4" : "bottom-6 right-6"
          )}
          aria-label="Quick Log"
        >
          <Plus className="h-6 w-6" />
        </button>
        {/* Mobile Bottom Nav */}
        {isMobile && (
          <nav className="fixed bottom-0 left-0 right-0 z-20 flex h-16 items-center justify-around border-t bg-background/95 backdrop-blur px-2">
            {mobileNavItems.map((item) => {
              const isActive = item.href === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "flex flex-col items-center gap-0.5 px-2 py-1 text-2xs transition-colors",
                    isActive
                      ? "text-ranch-gold font-medium"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        )}
        <QuickLogSheet open={quickLogOpen} onOpenChange={setQuickLogOpen} />
      </SidebarInset>
    </SidebarProvider>
  );
}